import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "lucide-react";

/**
 * Add-a-match form used by both the Admin page and the Editor dashboard.
 * Posts to POST /api/matches which is now editor-or-admin gated.
 */
export default function NewMatchForm({ onCreated }) {
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [kickoff, setKickoff] = useState("");
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/matches", {
        home_team: home,
        away_team: away,
        kickoff,
        match_date: date,
      });
      toast.success("Match added");
      setHome("");
      setAway("");
      setKickoff("");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-zinc-200 rounded-sm p-6 bg-white" data-testid="new-match-form">
      <div className="flex items-center gap-2 mb-4">
        <Plus className="h-4 w-4" />
        <h3 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Add a match</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Home team</Label>
          <Input
            required
            value={home}
            onChange={(e) => setHome(e.target.value)}
            data-testid="admin-home-input"
            className="mt-2 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Away team</Label>
          <Input
            required
            value={away}
            onChange={(e) => setAway(e.target.value)}
            data-testid="admin-away-input"
            className="mt-2 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Kickoff (e.g. 19:00)</Label>
          <Input
            required
            value={kickoff}
            onChange={(e) => setKickoff(e.target.value)}
            data-testid="admin-kickoff-input"
            className="mt-2 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Date</Label>
          <Input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="admin-date-input"
            className="mt-2 h-11 rounded-sm border-zinc-300"
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={saving}
        data-testid="admin-create-match-btn"
        className="mt-5 h-11 px-6 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold tracking-tight active:scale-[0.98]"
      >
        {saving ? "Adding…" : "Add match"}
      </Button>
    </form>
  );
}
